/**
 * Aufgabe — Detailseite in der Form der Vorlage.
 *
 * Kopf mit Kennung und Herkunft, Titel in 40px, der Zustand als Akzentknopf,
 * ein Zahlenband über der Tintenlinie, danach die Abschnitte als Rubriken mit
 * Haarlinien-Zeilen und rechts die schwarze Tafel mit Eigenschaften.
 *
 * Die Daten kommen aus `aufgaben.detail` und `aufgaben.optionen` — dieselben
 * Wege wie vor dem Umbau; geändert ist die Bauform, nicht die Datenlogik.
 */

import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  Bell,
  BellOff,
  Check,
  Copy,
  MoreHorizontal,
  RotateCcw,
} from "lucide-react";
import type { Rolle } from "@cubic/shared";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/DropdownMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Leerzustand } from "@/components/Leerzustand";
import { Label } from "@/components/Label";
import { Planabhaengigkeiten } from "@/components/Planabhaengigkeiten";
import { MehrLaden } from "@/components/MehrLaden";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Kennung, Zeilentitel } from "@/components/entwurf/Zeile";
import { Pruefquadrat } from "@/components/entwurf/Pruefquadrat";
import { Fortschrittslinie } from "@/components/entwurf/Fortschrittslinie";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import { RabeAvatar } from "@/components/RabeAvatar";
import { AufgabenBeschreibung } from "@/components/AufgabenBeschreibung";
import { istAbgeschlossen } from "@/lib/entwurf-zustaende";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { fehlerMeldung, useCursorListe, useIch } from "@/lib/haken";
import { darfAendern } from "@/lib/rollen";
import { initialen } from "@/lib/initialen";
import { cx, focusRing } from "@/lib/utils";
import type {
  Aufgabenart,
  Aufgabenbeziehungsart,
  Aufgabenbeziehungsrichtung,
} from "@cubic/shared";

/** „vor 9 Tagen" aus einem Datum. */
function vorZeit(wert: string | Date | null | undefined): string {
  if (!wert) return "";
  const tage = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(wert).getTime()) / (24 * 60 * 60 * 1_000),
    ),
  );
  if (tage === 0) return "heute";
  if (tage === 1) return "gestern";
  return `vor ${tage} Tagen`;
}

/** Tage bis zu einer Frist, für die Leitzahl. */
function tageBis(wert: string | Date | null | undefined): number | null {
  if (!wert) return null;
  return Math.max(
    0,
    Math.ceil(
      (new Date(wert).getTime() - Date.now()) / (24 * 60 * 60 * 1_000),
    ),
  );
}

export function AufgabeDetail() {
  const { id } = useParams({ from: "/_app/aufgaben/$id" });
  const client = useQueryClient();
  const ich = useIch();
  const [titel, setzeTitel] = useState<string | null>(null);
  const [inhalt, setzeInhalt] = useState<string | null>(null);
  const [kopiert, setzeKopiert] = useState(false);
  const [meldung, setzeMeldung] = useState<ReturnType<typeof fehlerMeldung> | null>(null);
  const [bearbeiteBeschreibung, setzeBearbeiteBeschreibung] = useState(false);
  const [verlaufReiter, setzeVerlaufReiter] = useState<"alles" | "kommentare" | "ereignisse">("alles");
  const [verknuepfenOffen, setzeVerknuepfenOffen] = useState(false);
  const [verknuepfenSuche, setzeVerknuepfenSuche] = useState("");
  const [beziehungArt, setzeBeziehungArt] =
    useState<Aufgabenbeziehungsart>("haengt_zusammen");
  const [beziehungRichtung, setzeBeziehungRichtung] =
    useState<Aufgabenbeziehungsrichtung>("aktuell_zu_anderer");
  const [beziehungZiel, setzeBeziehungZiel] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: schluessel.aufgabe(id),
    queryFn: () => api.aufgaben.detail.query({ id }),
  });
  const daten = detail.data;
  const aufgabe = daten?.aufgabe;
  const optionen = useQuery({
    queryKey: ["cubic", "aufgaben", "optionen", aufgabe?.projektId],
    queryFn: () => api.aufgaben.optionen.query({ projektId: aufgabe!.projektId }),
    enabled: aufgabe !== undefined,
  });

  const rolle = ich.data?.projekte.find(
    (projekt) => projekt.projektId === aufgabe?.projektId,
  )?.rolle as Rolle | undefined;
  const kann = darfAendern(rolle, { rechte: ich.data?.rechte });

  async function aktualisieren() {
    setzeMeldung(null);
    await Promise.all([
      detail.refetch(),
      client.invalidateQueries({ queryKey: ["cubic", "aufgaben"] }),
    ]);
  }

  const statusAendern = useMutation({
    mutationFn: (zustandId: string) =>
      api.aufgaben.status.mutate({
        id,
        zustandId,
        erwarteteVersion: aufgabe!.version,
      }),
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const zuweisungenAendern = useMutation({
    mutationFn: (personIds: string[]) =>
      api.aufgaben.zuweisungenSetzen.mutate({
        aufgabeId: id,
        personIds,
        erwarteteVersion: aufgabe!.version,
      }),
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const feldAendern = useMutation({
    mutationFn: (felder: {
      art?: Aufgabenart;
      prioritaet?: number;
      faelligAm?: Date | null;
      schaetzungMinuten?: number | null;
      arbeitspaketId?: string | null;
    }) =>
      api.aufgaben.aendern.mutate({
        id,
        erwarteteVersion: aufgabe!.version,
        ...felder,
      }),
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const beobachten = useMutation({
    mutationFn: () => {
      const personId = ich.data!.personId;
      const ids = daten!.beobachter.map((person) => person.id);
      const beobachtet = ids.includes(personId);
      return api.aufgaben.setzeBeobachter.mutate({
        aufgabeId: id,
        personIds: beobachtet
          ? ids.filter((wert) => wert !== personId)
          : [...ids, personId],
      });
    },
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const archivieren = useMutation({
    mutationFn: () =>
      aufgabe?.archiviertAm
        ? api.aufgaben.wiederherstellen.mutate({ id })
        : api.aufgaben.archivieren.mutate({ id }),
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const textSpeichern = useMutation({
    mutationFn: () =>
      api.aufgaben.aendern.mutate({
        id,
        titel: titel ?? aufgabe!.titel,
        inhalt: inhalt ?? aufgabe!.inhalt,
        erwarteteVersion: aufgabe!.version,
      }),
    onSuccess: async () => {
      setzeTitel(null);
      setzeInhalt(null);
      setzeBearbeiteBeschreibung(false);
      await aktualisieren();
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  /* Kriterien: erledigt umschalten und anlegen. */
  const kriteriumAendern = useMutation({
    mutationFn: (eingabe: {
      id: string;
      erwarteteVersion: number;
      erledigt?: boolean;
    }) => api.aufgaben.akzeptanzAendern.mutate(eingabe),
    onSuccess: aktualisieren,
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const [kriteriumText, setzeKriteriumText] = useState("");
  const kriteriumAnlegen = useMutation({
    mutationFn: (text: string) =>
      api.aufgaben.akzeptanzAnlegen.mutate({ aufgabeId: id, text }),
    onSuccess: async () => {
      setzeKriteriumText("");
      await aktualisieren();
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  /* Metarr-Lauf für die Kriterien-Zeile. */
  const metarrLauf = useQuery({
    queryKey: ["cubic", "metarr", "akzeptanzLauf", aufgabe?.id],
    queryFn: () => api.metarr.akzeptanzLauf.query({ aufgabeId: aufgabe!.id }),
    enabled: aufgabe !== undefined,
  });
  const metarrVorschlaege = useQuery({
    queryKey: ["cubic", "metarr", "vorschlaege", metarrLauf.data?.id],
    queryFn: () => api.metarr.vorschlaege.query({ entwurfId: metarrLauf.data!.id }),
    enabled: metarrLauf.data?.id !== undefined,
  });
  const metarrAnnehmen = useMutation({
    mutationFn: (vorschlagId: string) =>
      api.metarr.annehmen.mutate({ vorschlagId }),
    onSuccess: async () => {
      await Promise.all([aktualisieren(), metarrVorschlaege.refetch()]);
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const metarrAblehnen = useMutation({
    mutationFn: (vorschlagId: string) =>
      api.metarr.ablehnen.mutate({ vorschlagId }),
    onSuccess: () => void metarrVorschlaege.refetch(),
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  /* Unteraufgabe anlegen. */
  const [unteraufgabeText, setzeUnteraufgabeText] = useState("");
  const unteraufgabeAnlegen = useMutation({
    mutationFn: (titel: string) =>
      api.aufgaben.anlegen.mutate({
        projektId: aufgabe!.projektId,
        titel,
        elternId: id,
      }),
    onSuccess: async () => {
      setzeUnteraufgabeText("");
      await aktualisieren();
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  /* Verknüpfen: Suche + Kante setzen. */
  const verknuepfbare = useQuery({
    queryKey: ["cubic", "aufgaben", "verknuepfbare", id, verknuepfenSuche],
    queryFn: () =>
      api.aufgaben.verknuepfbare.query({
        aufgabeId: id,
        ...(verknuepfenSuche.trim() ? { suche: verknuepfenSuche.trim() } : {}),
      }),
    enabled: verknuepfenOffen,
  });
  const verknuepfen = useMutation({
    mutationFn: () =>
      api.aufgaben.beziehungSetzen.mutate({
        aufgabeId: id,
        andereAufgabeId: beziehungZiel!,
        art: beziehungArt,
        richtung: beziehungRichtung,
      }),
    onSuccess: async () => {
      setzeVerknuepfenOffen(false);
      setzeBeziehungZiel(null);
      await aktualisieren();
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  /* Verlauf: Kommentare + Ereignisse am Objekt. */
  const kommentare = useCursorListe({
    queryKey: ["cubic", "kommentare", "aufgabe", id],
    lade: (cursor) =>
      api.kommentare.liste.query({
        projektId: aufgabe!.projektId,
        betrifftArt: "aufgabe",
        betrifftId: id,
        cursor,
        grenze: 50,
      }),
    enabled: aufgabe !== undefined,
  });
  const ereignisse = useQuery({
    queryKey: ["cubic", "verlauf", "amObjekt", "aufgabe", id],
    queryFn: () =>
      api.verlauf.amObjekt.query({ art: "aufgabe", id, grenze: 50 }),
    enabled: aufgabe !== undefined,
  });
  const [kommentarText, setzeKommentarText] = useState("");
  const kommentarSchreiben = useMutation({
    mutationFn: (text: string) =>
      api.kommentare.schreiben.mutate({
        projektId: aufgabe!.projektId,
        betrifftArt: "aufgabe",
        betrifftId: id,
        inhalt: text,
      }),
    onSuccess: async () => {
      setzeKommentarText("");
      await Promise.all([kommentare.refetch(), ereignisse.refetch()]);
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  if (detail.isLoading) {
    return <Mikrolabel grad={12}>Lade Aufgabe …</Mikrolabel>;
  }
  if (detail.isError || !daten || !aufgabe) {
    return (
      <Leerzustand
        titel="Diesen Eintrag gibt es nicht."
        hinweis="Die Aufgabe ist nicht sichtbar oder existiert nicht."
      />
    );
  }

  const beobachtet = daten.beobachter.some(
    (person) => person.id === ich.data?.personId,
  );
  const titelWert = titel ?? aufgabe.titel;
  const inhaltWert = inhalt ?? aufgabe.inhalt;
  const textGeaendert = titel !== null || inhalt !== null;

  const kriterienErfuellt = daten.akzeptanzkriterien.filter(
    (kriterium) => kriterium.erledigt === 1,
  ).length;
  const kinderFertig = daten.kinder.filter(
    (kind) => {
      const zustand = optionen.data?.zustaende.find(
        (z) => z.id === kind.zustandId,
      );
      return zustand ? istAbgeschlossen(zustand.gruppe) : false;
    },
  ).length;

  const lauf = metarrLauf.data;
  const laufVorschlaege = metarrVorschlaege.data ?? [];
  const offeneMetarr = laufVorschlaege.filter(
    (vorschlag) => vorschlag.zustand === "offen",
  );
  const arbeitspaket = optionen.data?.arbeitspakete.find(
    (paket) => paket.id === aufgabe.arbeitspaketId,
  );
  const meilenstein = optionen.data?.meilensteine.find(
    (eintrag) =>
      eintrag.id === (arbeitspaket?.meilensteinId ?? aufgabe.meilensteinId),
  );
  const fachbeziehungen = daten.beziehungen.filter(
    (beziehung) => beziehung.art !== "blockiert",
  );
  const beziehungsoptionen = (optionen.data?.beziehungen ?? []).filter(
    (option) => option.art !== "blockiert",
  );

  async function linkKopieren() {
    await navigator.clipboard.writeText(globalThis.location.href);
    setzeKopiert(true);
    globalThis.setTimeout(() => setzeKopiert(false), 1_500);
  }

  return (
    /*
      Die Seite setzt ihre Abstände selbst (`lib/rahmen`): 44px oben, 48px
      rechts, 96px unten, 36px links — und die Tafel läuft bündig an die
      Fensterkante. Der Textblock steht **links** an dieser Kante, nicht
      mittig: Der Entwurf begrenzt ihn auf 720px, ohne ihn zu zentrieren, damit
      Titel, Rubriken und Zeilen dieselbe linke Kante haben wie die Brotkrume
      darüber.
    */
    /*
      Auf dem Handy untereinander statt umbrechend, und die Tafel **zuerst**.
      Vorher trug das Gerüst `flex-wrap`: Unter 1024px rutschte die Tafel als
      voller Desktop-Block unter den Text, und der Zustand einer Aufgabe stand
      hinter dem gesamten Fließtext. Wer auf dem Weg zur Arbeit nachsieht, was
      eine Aufgabe gerade ist, soll nicht scrollen müssen.
    */
    <div className="flex min-h-full flex-col items-stretch lg:flex-row lg:flex-wrap">
      <div
        data-testid="aufgabe-inhalt"
        className="min-w-[300px] flex-1 px-4 pb-24 pt-8 sm:px-6 lg:pb-24 lg:pl-9 lg:pr-12 lg:pt-11"
      >
        <div className="w-full max-w-[720px]">
          {meldung ? (
            <div className="mb-4">
              <Fehlerzeile
                meldung={meldung}
                onNeuLaden={() =>
                  void detail.refetch().then(() => setzeMeldung(null))
                }
              />
            </div>
          ) : null}

          {/* Kopf: Kennung, Kopieren, Herkunft. */}
          <div className="flex flex-wrap items-center gap-3">
            <Mikrolabel grad={12.5}>{daten.kennung}</Mikrolabel>
            <button
              type="button"
              onClick={() => void linkKopieren()}
              aria-label="Link kopieren"
              className={cx("text-tinte4 hover:text-tinte", focusRing)}
            >
              {kopiert ? (
                <Check className="size-3" aria-hidden="true" />
              ) : (
                <Copy className="size-3" aria-hidden="true" />
              )}
            </button>
            <span className="h-3 w-px bg-linie3" aria-hidden="true" />
            <Mikrolabel grad={12}>{daten.projekt.name}</Mikrolabel>
            {meilenstein ? (
              <>
                <span className="text-linie3" aria-hidden="true">/</span>
                <Mikrolabel grad={12}>{meilenstein.name}</Mikrolabel>
              </>
            ) : null}
            {arbeitspaket ? (
              <>
                <span className="text-linie3" aria-hidden="true">/</span>
                <Mikrolabel grad={12}>{arbeitspaket.name}</Mikrolabel>
              </>
            ) : null}
            <span className="h-3 w-px bg-linie3" aria-hidden="true" />
            <Mikrolabel grad={12}>
              angelegt {vorZeit(aufgabe.angelegtAm)}
            </Mikrolabel>
          </div>

          {/* Titel — direkt im Feld, wie der Entwurf ihn zeigt. */}
          <h1 className="mt-4">
            <Input
              id="aufgabe-titel"
              aria-label="Titel"
              value={titelWert}
              disabled={!kann || Boolean(aufgabe.archiviertAm)}
              inputClassName="h-auto border-0 bg-transparent p-0 font-extralight text-[40px] leading-[1.1] tracking-[-0.034em] text-tinte shadow-none focus:ring-0 dark:bg-transparent"
              onChange={(ereignis) => setzeTitel(ereignis.target.value)}
            />
          </h1>

          {/* Zustands-Button und Kopfaktionen. */}
          <div className="mt-[26px] flex flex-wrap items-center gap-2.5">
            <Select
              value={daten.zustand?.id ?? "__ohne__"}
              onValueChange={(zustandId) =>
                zustandId !== "__ohne__" && statusAendern.mutate(zustandId)
              }
            >
              <SelectTrigger
                className={cx(
                  "h-[31px] w-auto gap-2 border-0 bg-blue-600 px-[13px] font-mono text-[12px] font-medium text-white shadow-none ring-0",
                  "transition-colors duration-150 ease-aus hover:bg-blue-700 focus:ring-0",
                )}
                aria-label="Status"
              >
                <SelectValue placeholder="Ohne Zustand" />
              </SelectTrigger>
              <SelectContent align="start">
                {(optionen.data?.zustaende ?? []).map((zustand) => (
                  <SelectItem key={zustand.id} value={zustand.id}>
                    {zustand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {kann && !aufgabe.archiviertAm ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cx(
                        "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch",
                        focusRing,
                      )}
                    >
                      Zuweisen
                      <Mikrolabel grad={11}>
                        {daten.zuweisungen.map((p) => initialen(p.name)).join(", ")}
                      </Mikrolabel>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {(optionen.data?.personen ?? []).map((person) => {
                      const gesetzt = daten.zuweisungen.some(
                        (z) => z.id === person.id,
                      );
                      return (
                        <DropdownMenuItem
                          key={person.id}
                          onSelect={() =>
                            zuweisungenAendern.mutate(
                              gesetzt
                                ? daten.zuweisungen
                                    .filter((z) => z.id !== person.id)
                                    .map((z) => z.id)
                                : [...daten.zuweisungen.map((z) => z.id), person.id],
                            )
                          }
                        >
                          {person.name}
                          {gesetzt ? <Check className="ml-2 size-3" /> : null}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>

                <button
                  type="button"
                  onClick={() => beobachten.mutate()}
                  disabled={beobachten.isPending}
                  className={cx(
                    "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch",
                    focusRing,
                  )}
                >
                  {beobachtet ? <BellOff className="size-3.5" /> : <Bell className="size-3.5" />}
                  {beobachtet ? "Nicht folgen" : "Folgen"}
                  <Mikrolabel grad={11}>{daten.beobachter.length}</Mikrolabel>
                </button>

                <button
                  type="button"
                  onClick={() => setzeVerknuepfenOffen(true)}
                  className={cx(
                    "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch",
                    focusRing,
                  )}
                >
                  Verknüpfen
                </button>
              </>
            ) : null}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Weitere Aktionen"
                  className={cx(
                    "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch",
                    focusRing,
                  )}
                >
                  <MoreHorizontal className="size-3.5" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void linkKopieren()}>
                  <Copy className="mr-2 size-3.5" aria-hidden="true" />
                  {kopiert ? "Link kopiert" : "Link kopieren"}
                </DropdownMenuItem>
                {kann && !aufgabe.archiviertAm ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => archivieren.mutate()}
                    >
                      <Archive className="mr-2 size-3.5" aria-hidden="true" />
                      Archivieren
                    </DropdownMenuItem>
                  </>
                ) : null}
                {aufgabe.archiviertAm ? (
                  <DropdownMenuItem onSelect={() => archivieren.mutate()}>
                    <RotateCcw className="mr-2 size-3.5" aria-hidden="true" />
                    Wiederherstellen
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>

            <Mikrolabel grad={11} className="ml-auto flex-none">
              Fassung {aufgabe.version}
            </Mikrolabel>
            {textGeaendert && kann && !aufgabe.archiviertAm ? (
              <Button
                type="button"
                onClick={() => textSpeichern.mutate()}
                disabled={textSpeichern.isPending || !titelWert.trim()}
                data-testid="aufgabe-speichern"
                className="h-[31px] px-3.5 font-mono text-[11.5px]"
              >
                Speichern
              </Button>
            ) : null}
          </div>

          {/* Zahlenband. */}
          <Zahlenband className="mt-[34px]">
            {aufgabe.faelligAm ? (
              <Kennzahl
                label="Frist"
                grad={56}
                wert={
                  tageBis(aufgabe.faelligAm) === 0
                    ? "heute"
                    : `in ${tageBis(aufgabe.faelligAm)} Tagen`
                }
              />
            ) : (
              <Kennzahl label="Frist" grad={56} wert="—" />
            )}
            <Kennzahl
              label="Kriterien"
              grad={56}
              wert={`${kriterienErfuellt}/${daten.akzeptanzkriterien.length}`}
            />
            {aufgabe.schaetzungMinuten != null ? (
              <Kennzahl
                label="Aufwand"
                grad={56}
                wert={`${(aufgabe.schaetzungMinuten / 60).toLocaleString("de-DE", { maximumFractionDigits: 1 })} h`}
              />
            ) : null}
            <div className="flex min-w-[240px] flex-1 flex-wrap gap-x-[22px] gap-y-1.5 pb-2">
              <Nebenwert
                wert={kinderFertig}
                label={`von ${daten.kinder.length} Unteraufgaben fertig`}
              />
              <Nebenwert
                wert={fachbeziehungen.length}
                label="Beziehungen"
              />
            </div>
          </Zahlenband>

          {/* Beschreibung. */}
          <div className="mt-13">
            <Rubrik
              aktion={
                kann && !aufgabe.archiviertAm ? (
                  <button
                    type="button"
                    onClick={() => setzeBearbeiteBeschreibung((offen) => !offen)}
                    className={verweisKlassen({ grad: 12 })}
                  >
                    {bearbeiteBeschreibung ? "Schließen" : "Bearbeiten"}
                  </button>
                ) : undefined
              }
            >
              Beschreibung
            </Rubrik>
            {bearbeiteBeschreibung ? (
              <div className="pt-5">
                <Textarea
                  id="aufgabe-inhalt"
                  className="min-h-48 resize-y"
                  rows={9}
                  value={inhaltWert}
                  disabled={!kann || Boolean(aufgabe.archiviertAm)}
                  placeholder="Beschreibe Ziel, Kontext und wichtige Randbedingungen …"
                  onChange={(ereignis) => setzeInhalt(ereignis.target.value)}
                />
                <div className="mt-3 flex items-center gap-4">
                  <Button
                    type="button"
                    onClick={() => textSpeichern.mutate()}
                    disabled={textSpeichern.isPending || !titelWert.trim()}
                    data-testid="aufgabe-speichern"
                  >
                    Speichern
                  </Button>
                  {textGeaendert ? (
                    <Mikrolabel grad={11}>
                      Titel und Beschreibung werden zusammen gespeichert.
                    </Mikrolabel>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="pt-5">
                <AufgabenBeschreibung inhalt={inhaltWert} />
                {daten.eltern ? (
                  <p className="mt-4 text-[13.5px] font-light text-tinte3">
                    Unteraufgabe von{" "}
                    <Link
                      to="/aufgaben/$id"
                      params={{ id: daten.eltern.id }}
                      className={verweisKlassen({ grad: 12.5 })}
                    >
                      {daten.eltern.kennung} · {daten.eltern.titel}
                    </Link>
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {/* Akzeptanzkriterien. */}
          <div className="mt-13">
            <Rubrik
              anzahl={`${kriterienErfuellt} von ${daten.akzeptanzkriterien.length} erfüllt`}
              aktion={
                <Fortschrittslinie
                  anteil={
                    daten.akzeptanzkriterien.length === 0
                      ? 0
                      : (kriterienErfuellt / daten.akzeptanzkriterien.length) * 100
                  }
                  label="Akzeptanzkriterien"
                />
              }
            >
              Akzeptanzkriterien
            </Rubrik>

            {/* Metarr-Zeile: der neueste offene Vorschlag. */}
            {offeneMetarr.length > 0 && lauf ? (
              <div
                className="flex cursor-pointer items-start gap-3.5 border-b border-linie py-[15px] transition-colors duration-150 ease-aus hover:bg-wisch"
                onClick={() => {
                  /* Die Vorschläge liegen darunter; die Zeile führt sie an. */
                }}
              >
                <RabeAvatar groesse={20} className="mt-px" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] leading-[1.5] text-tinte">
                    Metarr schlägt {offeneMetarr.length}{" "}
                    {offeneMetarr.length === 1 ? "Kriterium" : "Kriterien"} vor.
                  </span>
                  {lauf.rueckfrage ? (
                    <span className="mt-2 block pl-3 text-[13px] font-light leading-[1.55] text-tinte3 shadow-[inset_2px_0_0_0_var(--akzent)]">
                      Rückfrage: {lauf.rueckfrage}
                    </span>
                  ) : null}
                  <span className="mt-2.5 block font-mono text-[11px] text-tinte4">
                    Lauf {lauf.id.slice(0, 8)} · {lauf.modell ?? "Modell"} ·{" "}
                    {lauf.dauerMs != null
                      ? `${Math.round(lauf.dauerMs / 1000)} s`
                      : "—"}
                  </span>
                </span>
                <span className="flex-none font-mono text-[11.5px] text-akzent">
                  Ansehen
                </span>
              </div>
            ) : null}

            {offeneMetarr.length > 0 ? (
              <div className="border-b border-linie">
                {offeneMetarr.map((vorschlag) => {
                  const feld = (vorschlag.felder as Record<string, { wert?: string }> | undefined)?.["text"];
                  return (
                    <div
                      key={vorschlag.id}
                      className="px-0 py-3.5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[14.5px] leading-[1.55] text-tinte">
                            {feld?.wert ?? vorschlag.aktion}
                          </p>
                          {vorschlag.begruendung ? (
                            <p className="mt-1.5 text-[13px] font-light leading-[1.55] text-tinte3">
                              {vorschlag.begruendung}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-none gap-2">
                          <Button
                            variant="secondary"
                            className="h-7 px-2.5 text-[12px]"
                            onClick={() => metarrAnnehmen.mutate(vorschlag.id)}
                            disabled={metarrAnnehmen.isPending}
                          >
                            Übernehmen
                          </Button>
                          <Button
                            variant="ghost"
                            className="h-7 px-2.5 text-[12px]"
                            onClick={() => metarrAblehnen.mutate(vorschlag.id)}
                            disabled={metarrAblehnen.isPending}
                          >
                            Ablehnen
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {daten.akzeptanzkriterien.map((kriterium) => (
              <div
                key={kriterium.id}
                className="border-b border-linie py-3.5"
              >
                <div className="flex items-start gap-3.5">
                  <Pruefquadrat
                    erfuellt={kriterium.erledigt === 1}
                    ariaLabel={`Kriterium ${kriterium.nummer} erledigt`}
                    onKlick={
                      kann && !aufgabe.archiviertAm
                        ? () =>
                            kriteriumAendern.mutate({
                              id: kriterium.id,
                              erwarteteVersion: kriterium.version,
                              erledigt: kriterium.erledigt !== 1,
                            })
                        : undefined
                    }
                    className="mt-[3px]"
                  />
                  <span
                    className={cx(
                      "flex-1 text-[14.5px] leading-[1.55]",
                      kriterium.erledigt === 1
                        ? "font-light text-tinte4"
                        : "text-tinte",
                    )}
                  >
                    {kriterium.text}
                  </span>
                </div>
                <Planabhaengigkeiten
                  objekt={{ art: "akzeptanzkriterium", id: kriterium.id }}
                  kannAendern={kann && !Boolean(aufgabe.archiviertAm)}
                  kompakt
                />
              </div>
            ))}

            {kann && !aufgabe.archiviertAm ? (
              <div className="flex items-center gap-2 pt-4">
                <Input
                  value={kriteriumText}
                  placeholder="Neues Kriterium …"
                  aria-label="Neues Akzeptanzkriterium"
                  className="h-8 max-w-sm"
                  onChange={(e) => setzeKriteriumText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && kriteriumText.trim()) {
                      kriteriumAnlegen.mutate(kriteriumText.trim());
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  className="h-8"
                  onClick={() => kriteriumAnlegen.mutate(kriteriumText.trim())}
                  disabled={!kriteriumText.trim() || kriteriumAnlegen.isPending}
                >
                  Kriterium
                </Button>
              </div>
            ) : null}
          </div>

          <Planabhaengigkeiten
            objekt={{ art: "aufgabe", id }}
            kannAendern={kann && !Boolean(aufgabe.archiviertAm)}
          />

          {/* Unteraufgaben. */}
          {!daten.eltern ? (
            <div className="mt-13">
            <Rubrik
              anzahl={`${kinderFertig} von ${daten.kinder.length} fertig`}
              aktion={
                <Fortschrittslinie
                  anteil={
                    daten.kinder.length === 0
                      ? 0
                      : (kinderFertig / daten.kinder.length) * 100
                  }
                  label="Unteraufgaben"
                />
              }
            >
              Unteraufgaben
            </Rubrik>
            {daten.kinder.map((kind) => {
              const zustand = optionen.data?.zustaende.find(
                (z) => z.id === kind.zustandId,
              );
              return (
                <Link
                  key={kind.id}
                  to="/aufgaben/$id"
                  params={{ id: kind.id }}
                  className={cx(
                    "flex h-[50px] cursor-pointer items-center gap-3.5 border-b border-linie transition-colors duration-150 ease-aus hover:bg-wisch",
                    focusRing,
                  )}
                >
                  <Kennung>{kind.kennung}</Kennung>
                  <Zeilentitel titel={kind.titel}>{kind.titel}</Zeilentitel>
                  <Mikrolabel grad={11} className="flex-none">
                    {kind.zustandId && zustand ? zustand.name : ""}
                  </Mikrolabel>
                </Link>
              );
            })}
            {kann && !aufgabe.archiviertAm ? (
              <div className="flex items-center gap-2 pt-4">
                <Input
                  value={unteraufgabeText}
                  placeholder="Unteraufgabe …"
                  aria-label="Unteraufgabe anlegen"
                  className="h-8 max-w-sm"
                  onChange={(e) => setzeUnteraufgabeText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && unteraufgabeText.trim()) {
                      unteraufgabeAnlegen.mutate(unteraufgabeText.trim());
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  className="h-8"
                  onClick={() => unteraufgabeAnlegen.mutate(unteraufgabeText.trim())}
                  disabled={!unteraufgabeText.trim() || unteraufgabeAnlegen.isPending}
                >
                  Unteraufgabe
                </Button>
              </div>
            ) : null}
            </div>
          ) : null}

          {/* Verlauf. */}
          <div className="mt-13">
            <Rubrik
              anzahl={`${kommentare.eintraege.length} Kommentare · ${ereignisse.data?.eintraege.length ?? 0} Ereignisse`}
            >
              Verlauf
            </Rubrik>
            <div className="flex items-center gap-[22px] border-b border-linie pb-3 pt-4 font-mono text-[11.5px]">
              {(
                [
                  ["alles", "Alles"],
                  ["kommentare", "Kommentare"],
                  ["ereignisse", "Ereignisse"],
                ] as const
              ).map(([wert, label]) => (
                <button
                  key={wert}
                  type="button"
                  onClick={() => setzeVerlaufReiter(wert)}
                  className={cx(
                    "cursor-pointer transition-colors duration-150 ease-aus",
                    verlaufReiter === wert
                      ? "font-medium text-tinte"
                      : "text-tinte4 hover:text-tinte",
                    focusRing,
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {verlaufReiter !== "ereignisse"
              ? kommentare.eintraege.map((kommentar) => (
                  <div
                    key={kommentar.id}
                    className="border-b border-linie py-5"
                  >
                    <div className="flex items-center gap-[11px]">
                      <span className="flex size-[23px] flex-none items-center justify-center bg-tinte font-mono text-[9.5px] text-papier">
                        {initialen(kommentar.autorName)}
                      </span>
                      <span className="text-[13.5px] font-medium text-tinte">
                        {kommentar.autorName}
                      </span>
                      <Mikrolabel grad={11.5}>
                        {new Date(kommentar.angelegtAm).toLocaleString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Mikrolabel>
                    </div>
                    <p className="mt-[11px] max-w-[58ch] whitespace-pre-wrap pl-[34px] text-[14.5px] font-light leading-[1.65] text-tinte2">
                      {kommentar.inhalt}
                    </p>
                  </div>
                ))
              : null}

            {verlaufReiter !== "ereignisse" ? (
              <MehrLaden
                hatMehr={kommentare.hasNextPage}
                laedt={kommentare.isFetchingNextPage}
                laden={() => void kommentare.fetchNextPage()}
                beschriftung="Ältere Kommentare laden"
              />
            ) : null}

            {verlaufReiter !== "kommentare"
              ? (ereignisse.data?.eintraege ?? []).map((ereignis) => (
                  <div
                    key={ereignis.id}
                    className="flex items-baseline gap-3.5 border-b border-linie py-3"
                  >
                    <span className="flex w-[23px] flex-none justify-center">
                      <span className="mt-1 size-[5px] bg-ruhe" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1 text-[13.5px] font-light text-tinte3">
                      <span className="font-medium text-tinte2">
                        {ereignis.akteurName ?? "System"}
                      </span>{" "}
                      {ereignis.beschreibung}
                    </span>
                    <Mikrolabel grad={11} className="flex-none">
                      {new Date(ereignis.geschehenAm).toLocaleTimeString("de-DE", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Mikrolabel>
                  </div>
                ))
              : null}

            {kann && !aufgabe.archiviertAm ? (
              <div className="mt-7 p-4 shadow-[inset_0_0_0_1px_var(--tinte4)]">
                <Input
                  value={kommentarText}
                  placeholder="Kommentar schreiben — @ erwähnt, # verweist auf eine Aufgabe"
                  aria-label="Kommentar schreiben"
                  className="h-8 border-0 bg-transparent shadow-none focus:ring-0 dark:bg-transparent"
                  onChange={(e) => setzeKommentarText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && kommentarText.trim()) {
                      kommentarSchreiben.mutate(kommentarText.trim());
                    }
                  }}
                />
                <div className="mt-4 flex items-center gap-4">
                  <Mikrolabel grad={11}>Markdown</Mikrolabel>
                  <Button
                    className="ml-auto h-[29px] px-3.5 font-mono text-[11.5px]"
                    onClick={() => kommentarSchreiben.mutate(kommentarText.trim())}
                    disabled={!kommentarText.trim() || kommentarSchreiben.isPending}
                  >
                    Senden
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Rechte Tafel. */}
      <div
        data-testid="aufgabe-eigenschaften"
        className="order-first min-w-0 bg-tafel px-4 pb-6 pt-5 text-tafel-tinte lg:order-none lg:min-w-[252px] lg:flex-[0_1_316px] lg:px-[26px] lg:pb-24 lg:pt-11"
      >
        <div className="border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Eigenschaften
        </div>

        {/*
          Auf dem Handy zwei Spalten mit der Beschriftung über dem Feld: Sieben
          volle Zeilen mit 84px-Beschriftung davor sind auf 375px eine Wand, und
          das Feld daneben bleibt zu schmal. Ab `lg` gilt wieder die Liste des
          Entwurfs.
        */}
        <div className="grid grid-cols-2 gap-x-4 lg:block">
        {/* Zustand */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Zustand
          </span>
          <Select
            value={daten.zustand?.id ?? "__ohne__"}
            onValueChange={(zustandId) =>
              zustandId !== "__ohne__" && statusAendern.mutate(zustandId)
            }
            disabled={!kann || Boolean(aufgabe.archiviertAm)}
          >
            <SelectTrigger
              className="h-[27px] border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none ring-0 focus:ring-0"
              aria-label="Zustand"
            >
              <SelectValue placeholder="Ohne Zustand" />
            </SelectTrigger>
            <SelectContent>
              {(optionen.data?.zustaende ?? []).map((zustand) => (
                <SelectItem key={zustand.id} value={zustand.id}>
                  {zustand.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Art */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Art
          </span>
          <Select
            value={aufgabe.art as Aufgabenart}
            onValueChange={(art) => feldAendern.mutate({ art: art as Aufgabenart })}
            disabled={!kann || Boolean(aufgabe.archiviertAm)}
          >
            <SelectTrigger
              className="h-[27px] border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none ring-0 focus:ring-0"
              aria-label="Art"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(optionen.data?.arten ?? []).map((art) => (
                <SelectItem key={art.wert} value={art.wert}>
                  {art.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Priorität */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Priorität
          </span>
          <Select
            value={aufgabe.prioritaet != null ? String(aufgabe.prioritaet) : "__ohne__"}
            onValueChange={(wert) =>
              wert !== "__ohne__" &&
              feldAendern.mutate({ prioritaet: Number(wert) })
            }
            disabled={!kann || Boolean(aufgabe.archiviertAm)}
          >
            <SelectTrigger
              className="h-[27px] border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none ring-0 focus:ring-0"
              aria-label="Priorität"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {(optionen.data?.prioritaeten ?? []).map((prioritaet) => (
                <SelectItem
                  key={prioritaet.wert}
                  value={String(prioritaet.wert)}
                >
                  {prioritaet.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fällig */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Fällig
          </span>
          <input
            type="date"
            aria-label="Fällig"
            disabled={!kann || Boolean(aufgabe.archiviertAm)}
            defaultValue={
              aufgabe.faelligAm
                ? new Date(aufgabe.faelligAm).toISOString().slice(0, 10)
                : ""
            }
            onChange={(e) =>
              feldAendern.mutate({
                faelligAm: e.target.value
                  ? new Date(`${e.target.value}T12:00:00.000Z`)
                  : null,
              })
            }
            className="h-[27px] w-full border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none focus:ring-0 disabled:opacity-60"
          />
        </div>

        {/* Schätzung */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Schätzung
          </span>
          <input
            type="number"
            min="0"
            step="0.5"
            aria-label="Schätzung"
            disabled={!kann || Boolean(aufgabe.archiviertAm)}
            defaultValue={
              aufgabe.schaetzungMinuten != null
                ? String(aufgabe.schaetzungMinuten / 60)
                : ""
            }
            onBlur={(e) =>
              feldAendern.mutate({
                schaetzungMinuten:
                  e.target.value.trim() === ""
                    ? null
                    : Math.round(Number(e.target.value) * 60),
              })
            }
            className="h-[27px] w-full border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none focus:ring-0 disabled:opacity-60"
          />
        </div>

        {/* Arbeitspaket — der Meilenstein wird daraus abgeleitet. */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Arbeitspaket
          </span>
          <Select
            value={aufgabe.arbeitspaketId ?? "__ohne__"}
            onValueChange={(arbeitspaketId) =>
              feldAendern.mutate({
                arbeitspaketId:
                  arbeitspaketId === "__ohne__" ? null : arbeitspaketId,
              })
            }
            disabled={
              !kann || Boolean(aufgabe.archiviertAm) || Boolean(daten.eltern)
            }
          >
            <SelectTrigger
              className="h-[27px] border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none ring-0 focus:ring-0"
              aria-label="Arbeitspaket"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ohne__">Rückstand</SelectItem>
              {(optionen.data?.arbeitspakete ?? []).map((paket) => (
                <SelectItem key={paket.id} value={paket.id}>
                  {optionen.data?.meilensteine.find(
                    (eintrag) => eintrag.id === paket.meilensteinId,
                  )?.name ?? "Meilenstein"}{" "}
                  · {paket.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Zuständig */}
        <div className="flex flex-col items-start gap-1 border-b border-tafel-wisch py-2.5 lg:flex-row lg:items-center lg:gap-3.5">
          <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
            Zuständig
          </span>
          <Select
            value={
              daten.zuweisungen[0]?.id ?? "__niemand__"
            }
            onValueChange={(personId) =>
              personId !== "__niemand__" &&
              zuweisungenAendern.mutate([personId])
            }
            disabled={!kann || Boolean(aufgabe.archiviertAm)}
          >
            <SelectTrigger
              className="h-[27px] border-0 bg-transparent p-0 font-mono text-[11.5px] text-tafel-tinte shadow-none ring-0 focus:ring-0"
              aria-label="Zuständig"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {(optionen.data?.personen ?? []).map((person) => (
                <SelectItem key={person.id} value={person.id}>
                  {person.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        </div>

        {daten.labels.length > 0 ? (
          <>
            <div className="mt-10 border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
              Labels
            </div>
            <div className="mt-4 flex flex-wrap gap-[7px]">
              {daten.labels.map((label) => (
                <span
                  key={label.id}
                  className="flex h-[23px] items-center px-[9px] font-mono text-[10.5px] text-tafel-tinte3 shadow-[inset_0_0_0_1px_var(--tafel-linie)]"
                >
                  {label.name}
                </span>
              ))}
            </div>
          </>
        ) : null}

        <div className="mt-10 border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Beziehungen
        </div>
        {fachbeziehungen.map((beziehung) => (
          <div
            key={beziehung.id}
            className="border-b border-tafel-wisch py-[13px]"
          >
            <Mikrolabel grad={11} className="text-tafel-tinte3">
              {beziehung.label}
            </Mikrolabel>
            <div className="mt-1.5 flex items-center gap-2.5">
              <span className="font-mono text-[11.5px] text-tafel-akzent">
                {beziehung.andere.kennung}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-light text-tafel-tinte3">
                {beziehung.andere.titel}
              </span>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setzeVerknuepfenOffen(true)}
          data-testid="beziehung-oeffnen"
          className={cx(
            "mt-3.5 cursor-pointer font-mono text-[11px] text-tafel-akzent hover:underline",
            focusRing,
          )}
        >
          Beziehung hinzufügen
        </button>

        <div className="mt-10 border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Beobachter
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-[7px]">
          {daten.beobachter.map((person) => (
            <span
              key={person.id}
              title={person.name}
              className="flex size-[26px] items-center justify-center font-mono text-[9.5px] text-tafel-tinte3 shadow-[inset_0_0_0_1px_var(--tafel-linie)]"
            >
              {initialen(person.name)}
            </span>
          ))}
          {kann && !aufgabe.archiviertAm ? (
            <button
              type="button"
              onClick={() => beobachten.mutate()}
              className={cx(
                "ml-1 cursor-pointer font-mono text-[11px] text-tafel-akzent hover:underline",
                focusRing,
              )}
            >
              {beobachtet ? "Nicht folgen" : "Folgen"}
            </button>
          ) : null}
        </div>

        <div className="mt-10 border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Herkunft
        </div>
        {[
          ["Angelegt", aufgabe.angelegtAm ? new Date(aufgabe.angelegtAm).toLocaleDateString("de-DE") : "—"],
          ["Geändert", aufgabe.geaendertAm ? new Date(aufgabe.geaendertAm).toLocaleDateString("de-DE") : "—"],
          ["Fassung", String(aufgabe.version)],
          ["Kennung", daten.kennung],
        ].map(([label, wert]) => (
          <div
            key={label}
            className="flex items-baseline gap-3.5 border-b border-tafel-wisch py-3"
          >
            <span className="flex-none font-mono text-[11.5px] text-tafel-tinte3 lg:w-[84px]">
              {label}
            </span>
            <span className="min-w-0 flex-1 text-[13.5px] font-light text-tafel-tinte">
              {wert}
            </span>
          </div>
        ))}
      </div>

      {/* Beziehungsdialog. */}
      <Dialog open={verknuepfenOffen} onOpenChange={setzeVerknuepfenOffen}>
        <DialogContent className="max-w-lg" data-testid="beziehung-dialog">
          <DialogHeader>
            <DialogTitle>Aufgabe verknüpfen</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="beziehung-art">Beziehungsart</Label>
                <Select
                  value={beziehungArt}
                  onValueChange={(wert) => {
                    const neueArt = wert as Aufgabenbeziehungsart;
                    setzeBeziehungArt(neueArt);
                    const option = beziehungsoptionen.find(
                      (o) => o.art === neueArt,
                    );
                    if (option?.symmetrisch) {
                      setzeBeziehungRichtung("aktuell_zu_anderer");
                    }
                  }}
                >
                  <SelectTrigger id="beziehung-art" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {beziehungsoptionen.map((option) => (
                      <SelectItem key={option.art} value={option.art}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="beziehung-richtung">Aussage</Label>
                <Select
                  value={beziehungRichtung}
                  disabled={
                    beziehungsoptionen.find(
                      (o) => o.art === beziehungArt,
                    )?.symmetrisch ?? false
                  }
                  onValueChange={(wert) =>
                    setzeBeziehungRichtung(
                      wert as Aufgabenbeziehungsrichtung,
                    )
                  }
                >
                  <SelectTrigger id="beziehung-richtung" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const gewaehlt = beziehungsoptionen.find(
                        (o) => o.art === beziehungArt,
                      );
                      return (
                        <>
                          <SelectItem value="aktuell_zu_anderer">
                            {gewaehlt?.aussageAusgehend ??
                              "hängt zusammen mit"}
                          </SelectItem>
                          {!gewaehlt?.symmetrisch ? (
                            <SelectItem value="anderer_zu_aktuell">
                              {gewaehlt?.aussageEingehend}
                            </SelectItem>
                          ) : null}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Input
              value={verknuepfenSuche}
              placeholder="Suchen …"
              aria-label="Aufgabe suchen"
              onChange={(e) => setzeVerknuepfenSuche(e.target.value)}
            />
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {(verknuepfbare.data ?? []).map((treffer) => {
                const gewaehlt = beziehungZiel === treffer.id;
                return (
                  <button
                    key={treffer.id}
                    type="button"
                    onClick={() => setzeBeziehungZiel(gewaehlt ? null : treffer.id)}
                    className={cx(
                      "flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-wisch",
                      gewaehlt && "bg-wisch",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate text-[13.5px] text-tinte">
                      {treffer.kuerzel}-{treffer.nummer} · {treffer.titel}
                    </span>
                    {gewaehlt ? (
                      <Check className="size-3.5 flex-none text-akzent" />
                    ) : null}
                  </button>
                );
              })}
              {verknuepfbare.data?.length === 0 ? (
                <Mikrolabel grad={11.5}>Keine Treffer.</Mikrolabel>
              ) : null}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzeVerknuepfenOffen(false)}>
              Abbrechen
            </Button>
            <Button
              data-testid="beziehung-speichern"
              onClick={() => void verknuepfen.mutate()}
              disabled={!beziehungZiel || verknuepfen.isPending}
            >
              Verknüpfen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
